<template>
  <div class="articles-page">
    <!-- 顶部导航组件 -->
    <TheHeader />
    
    <!-- 页面标题 -->
    <div class="page-title">
      <h1>推荐文章</h1>
      <p>健康知识，每日更新</p>
    </div>
    
    <!-- 页面内容区域 -->
    <div class="articles-content">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <el-icon class="loading-icon"><Loading /></el-icon>
        <p>正在加载文章信息...</p>
      </div>
      
      <!-- 错误提示 -->
      <div v-else-if="error" class="error-container">
        <el-icon class="error-icon"><WarningFilled /></el-icon>
        <p>{{ error }}</p>
        <button class="retry-btn" @click="loadArticles">重试</button>
      </div>
      
      <!-- 无数据提示 -->
      <div v-else-if="articles.length === 0" class="empty-container">
        <el-empty description="暂无文章数据" />
      </div>
      
      <!-- 文章列表 -->
      <div v-else class="articles-list">
        <div class="article-card" v-for="article in articles" :key="article.id">
          <div class="article-image">
            <img :src="getFullImageUrl(article.image)" :alt="article.title" />
          </div>
          <div class="article-content">
            <h2 class="article-title">{{ article.title }}</h2>
            <div class="article-meta">
              <span class="article-category">{{ article.category_name }}</span>
              <span class="article-date">{{ formatDate(article.createtime_text) }}</span>
            </div>
            <div class="article-description">
              {{ article.description }}
            </div>
            <div class="article-actions">
              <button class="read-more-btn" @click="showPaymentModal(article)">阅读全文</button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 分页 -->
      <div class="pagination-container" v-if="pagination.total > 0">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="pagination.total"
          :page-size="pagination.pageSize"
          :current-page="pagination.currentPage"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
    
    <!-- 底部信息组件 -->
    <TheFooter />
    
    <!-- 支付弹窗 -->
    <div class="payment-modal" v-if="showPayment">
      <div class="payment-content">
        <div class="payment-header">
          <h3>支付查看全文</h3>
          <button class="close-btn" @click="closePaymentModal">×</button>
        </div>
        <div class="payment-body">
          <p class="article-title">《{{ selectedArticle?.title }}》</p>
          <p class="payment-price">需支付 {{ selectedArticle?.price }} 元</p>
          <div class="qrcode-container">
            <img :src="generateQRCodeUrl()" alt="支付二维码" />
          </div>
          <p class="payment-tip">请使用微信或支付宝扫码支付</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 导入Element Plus组件
import { ElPagination, ElEmpty, ElIcon } from 'element-plus'
import { Loading, WarningFilled } from '@element-plus/icons-vue'
import 'element-plus/dist/index.css'

// 导入占位图片
import { placeholderImages } from '@/assets/placeholder.js'

// 导入自定义组件
import TheHeader from '@/components/TheHeader.vue'
import TheFooter from '@/components/TheFooter.vue'

// 导入API
import { getArticles } from '@/api/config'
import { BASE_URL } from '@/api/config'

export default {
  name: 'ArticleList',
  components: {
    ElPagination,
    ElEmpty,
    ElIcon,
    Loading,
    WarningFilled,
    TheHeader,
    TheFooter
  },
  data() {
    return {
      // 文章数据
      articles: [],
      loading: true,
      error: null,
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      
      // 占位图片
      placeholderImages,
      // 支付弹窗
      showPayment: false,
      selectedArticle: null
    }
  },
  methods: {
    // 加载文章数据
    async loadArticles() {
      this.loading = true;
      this.error = null;
      
      try {
        const { currentPage, pageSize } = this.pagination;
        const response = await getArticles({
          page: currentPage,
          pagesize: pageSize
        });
        
        if (response.code === 1) {
          this.articles = response.data.data;
          this.pagination.total = response.data.total;
        } else {
          this.error = response.msg || '获取文章数据失败';
        }
      } catch (error) {
        console.error('获取文章数据错误:', error);
        this.error = '网络错误，请稍后重试';
      } finally {
        this.loading = false;
      }
    },
    
    // 处理分页变化
    handleCurrentChange(page) {
      this.pagination.currentPage = page;
      this.loadArticles();
      // 滚动到页面顶部
      window.scrollTo(0, 0);
    },
    
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '';
      
      // 从字符串中提取日期部分 (忽略时间)
      const datePart = dateStr.split(' ')[0];
      return datePart;
    },
    
    // 获取完整图片URL
    getFullImageUrl(path) {
      if (!path) return this.placeholderImages.med1;
      
      // 如果已经是完整URL则直接返回
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      
      // 否则拼接基础URL
      return `${BASE_URL}${path}`;
    },
    
    // 显示支付弹窗
    showPaymentModal(article) {
      this.selectedArticle = article;
      this.showPayment = true;
    },
    
    // 关闭支付弹窗
    closePaymentModal() {
      this.showPayment = false;
      this.selectedArticle = null;
    },
    
    // 生成支付二维码URL
    generateQRCodeUrl() {
      // 使用本地二维码图片
      return require('@/assets/zhifu.png');
    }
  },
  created() {
    this.loadArticles();
  }
}
</script>

<style scoped>
.articles-page {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  color: #333;
  line-height: 1.6;
  background-color: #f9fafc;
}

.page-title {
  text-align: center;
  padding: 120px 20px 40px;
  background: linear-gradient(135deg, #3a8ee6, #42b983);
  color: white;
}

.page-title h1 {
  font-size: 2.8rem;
  margin-bottom: 15px;
}

.page-title p {
  font-size: 1.2rem;
  opacity: 0.9;
}

/* 内容区域样式 */
.articles-content {
  max-width: 1000px;
  margin: 0 auto;
  padding: 50px 20px 80px;
}

/* 加载和错误状态样式 */
.loading-container,
.error-container,
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  margin: 50px 0;
}

.loading-icon,
.error-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.loading-icon {
  color: #3a8ee6;
  animation: rotate 2s linear infinite;
}

.error-icon {
  color: #f56c6c;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.retry-btn {
  margin-top: 20px;
  background-color: #3a8ee6;
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
}

.retry-btn:hover {
  background-color: #2c6eb9;
  transform: translateY(-3px);
}

/* 文章列表样式 */
.articles-list {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.article-card {
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  display: flex;
}

.article-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
}

.article-image {
  flex: 0 0 300px;
  height: 240px;
  overflow: hidden;
}

.article-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
}

.article-card:hover .article-image img {
  transform: scale(1.08);
}

.article-content {
  flex: 1;
  padding: 25px;
  display: flex;
  flex-direction: column;
}

.article-title {
  font-size: 1.5rem;
  margin-bottom: 12px;
  color: #333;
  font-weight: 600;
}

.article-meta {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  font-size: 0.9rem;
}

.article-category {
  color: #3a8ee6;
  font-weight: 600;
}

.article-date {
  color: #999;
}

.article-description {
  color: #666;
  line-height: 1.8;
  margin-bottom: 20px;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.read-more-btn {
  align-self: flex-start;
  background-color: #3a8ee6;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
}

.read-more-btn:hover {
  background-color: #2c6eb9;
  transform: translateY(-2px);
}

/* 分页样式 */
.pagination-container {
  margin-top: 50px;
  display: flex;
  justify-content: center;
}

/* 响应式设计 */
@media (max-width: 992px) {
  .article-card {
    flex-direction: column;
  }
  
  .article-image {
    flex: 0 0 auto;
    height: 200px;
  }
}

@media (max-width: 576px) {
  .page-title h1 {
    font-size: 2rem;
  }
  
  .article-title {
    font-size: 1.3rem;
  }
  
  .article-content {
    padding: 20px;
  }
}

/* 支付弹窗样式 */
.payment-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.payment-content {
  background-color: white;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.payment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.payment-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
}

.payment-body {
  padding: 20px;
  text-align: center;
}

.payment-body .article-title {
  font-size: 16px;
  margin-bottom: 15px;
  color: #333;
}

.payment-price {
  font-size: 18px;
  font-weight: bold;
  color: #f56c6c;
  margin-bottom: 20px;
}

.qrcode-container {
  margin: 0 auto 20px;
  width: 200px;
  height: 200px;
}

.qrcode-container img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.payment-tip {
  color: #666;
  font-size: 14px;
}

/* 阅读更多按钮样式 */
.read-more-btn {
  background-color: #409eff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.read-more-btn:hover {
  background-color: #66b1ff;
}
</style> 